import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './components/Toast';
import { setTokenGetter } from './utils/api';
import { redirectToHostedUI } from './utils/cognitoAuth';
import AppShell        from './components/AppShell';
import CallbackPage    from './pages/CallbackPage';
import DashboardPage   from './pages/DashboardPage';
import PostsPage       from './pages/PostsPage';
import BrandVoicePage  from './pages/BrandVoicePage';
import BrandImagesPage from './pages/BrandImagesPage';
import EscalationsPage from './pages/EscalationsPage';
import LeadsPage       from './pages/LeadsPage';
import CommentsPage    from './pages/CommentsPage';
import MessagesPage    from './pages/MessagesPage';
import AuditLogPage          from './pages/AuditLogPage';
import SettingsPage          from './pages/SettingsPage';
import { api } from './utils/api';

// Shown briefly while attempting a silent Cognito SSO check, then redirects.
function CognitoRedirect() {
  useEffect(() => {
    // Try silent auth first: if the user is already logged in via notes.homecareofmi.com,
    // Cognito will return a code without showing the login page (true SSO).
    // On failure Cognito returns error=login_required and CallbackPage handles full login.
    const alreadyTried = sessionStorage.getItem('sso_silent_tried');
    if (!alreadyTried) {
      sessionStorage.setItem('sso_silent_tried', '1');
      redirectToHostedUI({ silent: true, returnPath: window.location.pathname });
    } else {
      sessionStorage.removeItem('sso_silent_tried');
      redirectToHostedUI({ returnPath: window.location.pathname });
    }
  }, []);

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--muted)' }}>
      Signing in…
    </div>
  );
}

function ProtectedApp() {
  const { user, token, loading } = useAuth();
  const [badges, setBadges] = useState({});

  // Set token synchronously so first API calls from child components get it
  if (token) setTokenGetter(() => token);

  useEffect(() => {
    if (!user) return;
    const refresh = () =>
      api.getDashboard()
        .then(d => setBadges({ escalations: d.open_escalations || 0, leads: d.new_leads || 0 }))
        .catch(() => {});
    refresh();
    const id = setInterval(refresh, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [user]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--muted)' }}>
      Loading…
    </div>
  );

  if (!user) {
    return (
      <Routes>
        <Route path="/callback" element={<CallbackPage />} />
        <Route path="*"         element={<CognitoRedirect />} />
      </Routes>
    );
  }

  return (
    <AppShell badges={badges}>
      <Routes>
        <Route path="/"            element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard"   element={<DashboardPage />} />
        <Route path="/posts"       element={<PostsPage />} />
        <Route path="/brand-voice"   element={<BrandVoicePage />} />
        <Route path="/brand-images" element={<BrandImagesPage />} />
        <Route path="/escalations"  element={<EscalationsPage />} />
        <Route path="/leads"       element={<LeadsPage />} />
        <Route path="/comments"    element={<CommentsPage />} />
        <Route path="/messages"    element={<MessagesPage />} />
        <Route path="/audit"       element={<AuditLogPage />} />
        <Route path="/settings"    element={<SettingsPage />} />
        <Route path="/callback"    element={<Navigate to="/dashboard" replace />} />
        <Route path="*"            element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AppShell>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <ProtectedApp />
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
